"use client";

import { useMemo, useState } from "react";
import { Frame, Slider, Verdict, Note, money } from "./parts";

const L = {
    fr: {
        title: "Remboursé avant ou après le départ du client ?",
        cac: "Coût d'acquisition d'un client",
        margin: "Marge mensuelle par client",
        churn: "Résiliations par mois",
        payback: "Remboursement du coût d'acquisition",
        lifetime: "Durée de vie moyenne",
        ratio: "Ratio LTV / CAC",
        months: "mois",
        never: "jamais",
        good: "Le coût est remboursé bien avant le départ : vous pouvez accélérer l'acquisition.",
        warn: "Le remboursement arrive peu avant le départ moyen. Un mois de retard suffit à basculer.",
        bad: "Le client moyen part avant d'avoir remboursé son acquisition : chaque nouveau client creuse le trou.",
        note: "Les deux durées se lisent sur la même échelle, c'est tout l'intérêt. Un ratio LTV/CAC flatteur ne dit rien si le remboursement arrive après le départ.",
    },
    en: {
        title: "Repaid before or after the customer leaves?",
        cac: "Customer acquisition cost",
        margin: "Monthly margin per customer",
        churn: "Cancellations per month",
        payback: "Acquisition cost repaid in",
        lifetime: "Average lifetime",
        ratio: "LTV / CAC ratio",
        months: "months",
        never: "never",
        good: "The cost is repaid well before departure: you can accelerate acquisition.",
        warn: "Payback lands just before the average departure. One month of slippage flips it.",
        bad: "The average customer leaves before repaying their acquisition: each new customer digs the hole deeper.",
        note: "Both durations sit on the same scale, which is the whole point. A flattering LTV/CAC ratio says nothing if payback arrives after departure.",
    },
};

export default function PaybackVsLifetime({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    const [cac, setCac] = useState(180);
    const [margin, setMargin] = useState(28);
    const [churn, setChurn] = useState(5);

    const r = useMemo(() => {
        const lifetime = 100 / churn;
        const payback = margin > 0 ? cac / margin : Infinity;
        const ltv = margin * lifetime;
        return { lifetime, payback, ratio: cac > 0 ? ltv / cac : 0 };
    }, [cac, margin, churn]);

    const scale = Math.max(r.lifetime, Math.min(r.payback, 60), 6) * 1.12;
    const w = (m) => `${Math.min(100, (m / scale) * 100)}%`;

    const tone =
        r.payback > r.lifetime ? "bad" : r.lifetime > r.payback * 1.5 ? "good" : "warn";

    const bar = (label, value, done, colour) => (
        <div>
            <div className="flex justify-between items-baseline mb-1.5 gap-3">
                <span className="text-sm text-gray-600 dark:text-lightBlue">{label}</span>
                <span className="text-sm font-bold tabular-nums dark:text-white shrink-0">
                    {done ? `${Math.round(value)} ${t.months}` : t.never}
                </span>
            </div>
            <div className="h-4 rounded-full bg-gray-200/70 dark:bg-white/10 overflow-hidden">
                <div className={`h-full rounded-full ${colour}`} style={{ width: done ? w(value) : "100%" }} />
            </div>
        </div>
    );

    return (
        <Frame icon="clock-circle-linear" title={t.title}>
            <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-4">
                    <Slider label={t.cac} value={cac} onChange={setCac} min={20} max={600} step={10} display={money(cac, lang)} />
                    <Slider label={t.margin} value={margin} onChange={setMargin} min={3} max={120} step={1} display={money(margin, lang)} />
                    <Slider label={t.churn} value={churn} onChange={setChurn} min={1} max={20} step={1} display={`${churn} %`} />
                </div>

                <div className="space-y-5 sm:pt-1">
                    {bar(t.payback, r.payback, r.payback <= 120, "bg-primary")}
                    {bar(t.lifetime, r.lifetime, true, "bg-gray-400 dark:bg-white/35")}

                    <div className="rounded-xl bg-gray-100/70 dark:bg-white/[0.04] p-4 flex justify-between items-baseline">
                        <span className="text-sm text-gray-600 dark:text-lightBlue">{t.ratio}</span>
                        <span className="text-2xl font-bold tabular-nums dark:text-white">
                            {r.ratio.toFixed(1)}
                        </span>
                    </div>
                </div>
            </div>

            <Verdict tone={tone}>{t[tone]}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
